import { setCookie } from "@insite/client-framework/Common/Cookies";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import CarosulArrow from "@tIntermatic/Icons/CarosulArrow";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
    productsDataView: getProductListDataView(state),
    searchQuery: state.pages.productList.parameter?.search,
    searchWithinQuery: state.pages.productList?.filteredApiParameter?.searchWithin,
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {
    pagination: {
        cssOverrides: {
            perPageSelect: css`
                display: none;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    )};
            `,
            pagination: css`
                margin: 0px !important;
                > nav {
                    > ul {
                        > li {
                            button[data-test-selector="pagination_firstButton"],
                            [data-test-selector="pagination_lastButton"] {
                                display: none;
                            }
                            button[data-test-selector="pagination_previousButton"],
                            [data-test-selector="pagination_nextButton"] {
                                margin: 0px;
                                width: 20px;
                                padding: 0px !important;
                                border: none !important;
                            }
                            button[data-test-selector="pagination_previousButton"] {
                                > span {
                                    transform: rotate(180deg);
                                }
                            }
                            button {
                                padding: 0 4px;
                                width: auto;
                                @media (max-width: 412px) {
                                    padding: 0;
                                }
                            }
                        }
                    }
                }
            `,

            currentButton: css`
                border: 1px solid #cccccc;
                background-color: #ffffff;
                padding: 7px;
                padding-right: 10px;
                box-sizing: border-box;
                height: 40px;
                width: 26px;
                > span {
                    font-size: 18px;
                    font-weight: 600;
                    letter-spacing: 0;
                    line-height: 22px;
                    color: #33333a;
                }
                &:hover {
                    background-color: #ffffff !important;
                    color: #33333a !important;
                }
            `,
        },
        navIconsSrc: {
            previousPage: CarosulArrow,
            nextPage: CarosulArrow,
        },
        buttonProps: {
            css: css`
                border: none;
                cursor: pointer;
                &:hover {
                    background-color: #ffffff !important;
                    color: #33333a !important;
                }
            `,
        },
    },
};

const styles = paginationStyles;

const ProductListPagination = ({
    addProductFilters,
    pagination,
    productsDataView,
    hidePagination,
    searchQuery,
    searchWithinQuery,
}: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);

    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }
    const totalItemCount = pagination?.totalItemCount;
    const displayView = () => {
        // if (totalItemCount > 100) {
        return (
            <Pagination
                {...styles.pagination}
                resultsCount={pagination.totalItemCount}
                currentPage={pagination.page}
                resultsPerPage={pagination.pageSize}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={changePage}
                onChangeResultsPerPage={changeResultsPerPage}
                pageSizeCookie="ProductList-PageSize"
                resultsPerPageLabel=" "
                data-test-selector="productList_pagination"
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
            />
        );
    };

    const conditionalView = () => {
        if (searchWithinQuery) {
            return displayView();
        }
        if (searchQuery && productsDataView.value?.length === 0) {
            return null;
        }
        return displayView();
    };
    return conditionalView();
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
